import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserQRCodeReader } from '@zxing/browser';
import { DecodeHintType } from '@zxing/library';
import qrAuthService from './services/qrAuthService';
import {
  getCameraPreference,
  listVideoInputDevices,
  saveCameraPreference,
} from './utils/cameraPreference';

const REAR_CAMERA_REGEX = /(back|rear|environment|trasera|posterior|traseira)/i;

const buildVideoConstraints = (cameraId) => ({
  deviceId: cameraId ? { exact: cameraId } : undefined,
  facingMode: cameraId ? undefined : { ideal: 'environment' },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
});

const waitForNextPaint = () => new Promise((resolve) => {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => resolve());
    return;
  }

  setTimeout(resolve, 0);
});

const pickInitialCameraId = (cameras) => {
  const preferredCamera = getCameraPreference();
  if (preferredCamera?.deviceId && cameras.some((camera) => camera.deviceId === preferredCamera.deviceId)) {
    return preferredCamera.deviceId;
  }

  const rearCamera = cameras.find((camera) => REAR_CAMERA_REGEX.test(camera.label || ''));
  return rearCamera?.deviceId || cameras[0]?.deviceId || null;
};

const DEFAULT_SCANNER_CONTENT = Object.freeze({
  heroEyebrow: 'Entrar a partida',
  heroTitle: 'Accede a tu partida desde el movil',
  heroDescription: 'Usa la camara del navegador para escanear el QR de la tablet del admin y abrir tu partida de liga desde el movil.',
  idleEyebrow: 'Acceso rapido a partida',
  idleTitle: 'Escanea el QR de la tablet',
  idleDescription: 'Pulsa en abrir camara y apunta al QR que aparece en la tablet del admin para abrir tu partida de liga.',
  steps: ['1. Abre camara', '2. Enfoca la tablet', '3. Entra en la partida'],
  openLabel: 'Abrir camara',
  backLabel: 'Volver a ligas',
  note: 'Si el navegador te pide permiso, aceptalo para poder abrir la camara.',
  scannerEyebrow: 'Camara activa',
  scannerTitle: 'Apunta al QR de la tablet',
  switchCameraLabel: 'Cambiar camara',
  openingCameraText: 'Abriendo camara...',
  authenticatingText: 'Validando acceso con la tablet...',
  compactNote: 'Consejo: si usas la webcam del portatil, acerca la tablet, sube su brillo y evita reflejos. Si ves la camara equivocada, pulsa en cambiar camara.',
  successTitle: 'Acceso enviado',
  errorTitle: 'No se pudo completar el acceso',
  retryLabel: 'Reintentar',
});

const buildDefaultSuccessMessage = (result) => {
  const barName = result?.barInfo?.name ? ` con ${result.barInfo.name}` : '';
  return `La tablet${barName} ya puede acceder a tus partidas de liga.`;
};

export default function LeagueQRScanner({ onBack, content = DEFAULT_SCANNER_CONTENT, successMessageBuilder = buildDefaultSuccessMessage }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const statusRef = useRef('idle');
  const resolvedContent = {
    ...DEFAULT_SCANNER_CONTENT,
    ...content,
    steps: content?.steps || DEFAULT_SCANNER_CONTENT.steps,
  };
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const stopScanner = useCallback(async () => {
    const controls = controlsRef.current;
    const reader = readerRef.current;
    const videoElement = videoRef.current;

    controlsRef.current = null;
    readerRef.current = null;

    try {
      controls?.stop();
    } catch {}

    try {
      reader?.reset?.();
    } catch {}

    if (videoElement) {
      try {
        const stream = videoElement.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach((track) => track.stop());
        }
      } catch {}

      videoElement.srcObject = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      void stopScanner();
    };
  }, [stopScanner]);

  const refreshCameraList = useCallback(async () => {
    try {
      const cameras = await listVideoInputDevices();
      setAvailableCameras(cameras);
      return cameras;
    } catch {
      setAvailableCameras([]);
      return [];
    }
  }, []);

  const handleScanSuccess = useCallback(async (decodedText) => {
    if (statusRef.current === 'authenticating' || statusRef.current === 'success') {
      return;
    }

    setStatus('authenticating');
    setError('');

    try {
      const result = await qrAuthService.authenticateWithQR(decodedText);
      await stopScanner();

      setSuccessMessage(successMessageBuilder(result));
      setStatus('success');
    } catch (scanError) {
      await stopScanner();
      setError(scanError?.message || 'No se pudo autenticar con el QR.');
      setStatus('error');
    }
  }, [stopScanner, successMessageBuilder]);

  const startScanner = useCallback(async (cameraId, cameras = []) => {
    setStatus('starting');
    setError('');
    setSuccessMessage('');

    await stopScanner();

    const videoElement = videoRef.current;
    if (!videoElement) {
      setError('No se pudo preparar el visor de la camara.');
      setStatus('error');
      return;
    }

    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);

    const reader = new BrowserQRCodeReader(hints, {
      delayBetweenScanAttempts: 120,
    });
    readerRef.current = reader;

    try {
      const initialCameras = cameras.length > 0 ? cameras : await refreshCameraList();
      const nextCameraId = cameraId || pickInitialCameraId(initialCameras);
      const controls = await reader.decodeFromConstraints(
        {
          audio: false,
          video: buildVideoConstraints(nextCameraId),
        },
        videoElement,
        (result) => {
          if (!result) {
            return;
          }

          void handleScanSuccess(result.getText());
        }
      );

      controlsRef.current = controls;

      const resolvedCameras = await refreshCameraList();
      const activeCameras = resolvedCameras.length > 0 ? resolvedCameras : initialCameras;
      const activeTrack = videoElement.srcObject?.getVideoTracks?.()[0] || null;
      const trackDeviceId = activeTrack?.getSettings?.().deviceId || null;
      const resolvedCameraId = nextCameraId || trackDeviceId || activeCameras[0]?.deviceId || null;
      const selectedCamera = activeCameras.find((camera) => camera.deviceId === resolvedCameraId);

      if (resolvedCameraId) {
        saveCameraPreference({
          deviceId: resolvedCameraId,
          label: selectedCamera?.label || '',
        });
      }

      setSelectedCameraId(resolvedCameraId);
      setStatus('scanning');
    } catch (startError) {
      const hasFallback = !cameraId && cameras.length > 0;
      if (hasFallback) {
        const fallbackCameraId = pickInitialCameraId(cameras);
        if (fallbackCameraId) {
          await startScanner(fallbackCameraId, cameras);
          return;
        }
      }

      await stopScanner();

      if (startError?.name === 'NotAllowedError') {
        setError('Has bloqueado la cámara. Permítela en el navegador y vuelve a intentarlo.');
      } else if (startError?.name === 'NotReadableError') {
        setError('La cámara está ocupada por otra aplicación o pestaña. Cierra ese uso y vuelve a intentarlo.');
      } else if (startError?.name === 'NotFoundError') {
        setError('No se ha detectado ninguna cámara disponible en este equipo.');
      } else {
        setError(startError?.message || 'No se pudo iniciar la cámara.');
      }

      setStatus('error');
    }
  }, [handleScanSuccess, refreshCameraList, stopScanner]);

  const openCamera = useCallback(async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setError('Tu navegador no permite acceder a la cámara.');
      setStatus('error');
      return;
    }

    setError('');
    setSuccessMessage('');
    setStatus('starting');

    try {
      const hasActiveSession = await qrAuthService.ensureActiveSession();
      if (!hasActiveSession) {
        return;
      }

      const cameras = await refreshCameraList();
      const initialCameraId = pickInitialCameraId(cameras);
      await waitForNextPaint();
      await startScanner(initialCameraId, cameras);
    } catch (cameraError) {
      if (cameraError?.name === 'NotAllowedError') {
        setError('Has bloqueado la cámara. Permítela en el navegador y vuelve a intentarlo.');
      } else if (cameraError?.name === 'NotReadableError') {
        setError('La cámara está ocupada por otra aplicación o pestaña. Cierra ese uso y vuelve a intentarlo.');
      } else {
        setError('No se pudo acceder a la cámara. Comprueba los permisos del navegador y vuelve a intentarlo.');
      }
      setStatus('error');
    }
  }, [refreshCameraList, startScanner]);

  const handleSwitchCamera = useCallback(async () => {
    if (availableCameras.length < 2) {
      return;
    }

    const currentIndex = availableCameras.findIndex((camera) => camera.deviceId === selectedCameraId);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % availableCameras.length : 0;
    const nextCamera = availableCameras[nextIndex];

    if (!nextCamera?.deviceId) {
      return;
    }

    await startScanner(nextCamera.deviceId, availableCameras);
  }, [availableCameras, selectedCameraId, startScanner]);

  const renderIdleState = () => (
    <div className="league-qr-card">
      <div className="league-qr-copy">
        <span className="league-qr-eyebrow">{resolvedContent.idleEyebrow}</span>
        <h3>{resolvedContent.idleTitle}</h3>
        <p>
          {resolvedContent.idleDescription}
        </p>

        <div className="league-qr-flow" aria-hidden="true">
          {resolvedContent.steps.map((step) => (
            <span key={step} className="league-qr-flow-step">{step}</span>
          ))}
        </div>
      </div>

      <div className="league-qr-actions">
        <button className="league-qr-primary" onClick={openCamera}>
          {resolvedContent.openLabel}
        </button>
        <button className="league-qr-secondary" onClick={onBack}>
          {resolvedContent.backLabel}
        </button>
      </div>

      <div className="league-qr-note">
        {resolvedContent.note}
      </div>
    </div>
  );

  const renderScannerState = () => (
    <div className="league-qr-card scanner">
      <div className="league-qr-scanner-toolbar">
        <div>
          <span className="league-qr-eyebrow">{resolvedContent.scannerEyebrow}</span>
          <h3>{resolvedContent.scannerTitle}</h3>
        </div>
        {availableCameras.length > 1 && (
          <button className="league-qr-secondary small" onClick={handleSwitchCamera}>
            {resolvedContent.switchCameraLabel}
          </button>
        )}
      </div>

      <div className="league-qr-reader-shell">
        <video ref={videoRef} className="league-qr-reader" muted playsInline />
        {status === 'starting' && (
          <div className="league-qr-reader-overlay">
            <div className="league-qr-spinner" />
            <p>{resolvedContent.openingCameraText}</p>
          </div>
        )}
        {status === 'authenticating' && (
          <div className="league-qr-reader-overlay">
            <div className="league-qr-spinner" />
            <p>{resolvedContent.authenticatingText}</p>
          </div>
        )}
      </div>

      <div className="league-qr-note compact">
        {resolvedContent.compactNote}
      </div>
    </div>
  );

  const renderStatusCard = () => {
    if (status === 'success') {
      return (
        <div className="league-qr-feedback success">
          <h4>{resolvedContent.successTitle}</h4>
          <p>{successMessage}</p>
          <button className="league-qr-primary" onClick={onBack}>
            {resolvedContent.backLabel}
          </button>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="league-qr-feedback error">
          <h4>{resolvedContent.errorTitle}</h4>
          <p>{error}</p>
          <div className="league-qr-actions stacked">
            <button className="league-qr-primary" onClick={openCamera}>
              {resolvedContent.retryLabel}
            </button>
            <button className="league-qr-secondary" onClick={onBack}>
              {resolvedContent.backLabel}
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="league-qr-panel">
      <div className="league-qr-hero">
        <div>
          <span className="league-qr-eyebrow">{resolvedContent.heroEyebrow}</span>
          <h2>{resolvedContent.heroTitle}</h2>
          <p>
            {resolvedContent.heroDescription}
          </p>
        </div>
      </div>

      {status === 'idle' && renderIdleState()}
      {(status === 'starting' || status === 'scanning' || status === 'authenticating') && renderScannerState()}
      {renderStatusCard()}
    </div>
  );
}
